// Transaction Wizard — where a run lives between screens (docs/plans/
// transaction-wizard.md §9).
//
// MEMORY (every run): a module-level map, like the Document Form's
// er/draftStore.ts — a view switch unmounts the runner and must not lose the
// pick in progress; a reload does, by design. Nothing silently resurrects an
// old run against a record that has since changed.
//
// SERVER (`suspend: "server"`): the `wizard_*` run-store operations on the
// execute funnel, addressed to the WIZARD object (so the host gates them on
// its `run` right). A run is written on every call, every suspend, and every
// step change of a run that has been written once — the server copy is what
// the Work List resumes from.

import type { TxnScreenRuntime } from '../txn/screen/contract'
import type {
  WizardRun, WizardRunAccess, WizardRunHandbackRequest, WizardRunListResponse, WizardRunLoadResponse,
} from './contract'

const runs = new Map<string, WizardRun>()
/** Launch identity (project + wizard + inputs) → the run started for it. */
const slots = new Map<string, string>()

export function slotKey(project: string, wizard: string, inputs?: Record<string, unknown>): string {
  const keys = Object.keys(inputs || {}).sort()
  const norm = keys.map(k => [k, (inputs as Record<string, unknown>)[k]])
  return `${project} ${wizard} ${JSON.stringify(norm)}`
}

export function readMemoryRun(runId: string): WizardRun | undefined {
  return runs.get(runId)
}

export function writeMemoryRun(run: WizardRun, slot?: string): void {
  runs.set(run.run_id, run)
  if (slot) slots.set(slot, run.run_id)
}

/** The live run a launch identity already has in this tab, if any. */
export function memoryRunFor(slot: string): WizardRun | undefined {
  const id = slots.get(slot)
  const run = id ? runs.get(id) : undefined
  if (!run) return undefined
  if (run.status === 'completed' || run.status === 'cancelled' || run.status === 'expired') return undefined
  return run
}

/** Forget a run — on finish and on an explicit cancel, never on unmount. */
export function clearMemoryRun(runId: string): void {
  runs.delete(runId)
  for (const [k, v] of slots) if (v === runId) slots.delete(k)
}

export interface ServerRunStore {
  save(run: WizardRun): Promise<string>
  /** The run and who the caller is to it (an assignee runs the top frame only). */
  load(runId: string): Promise<{ run: WizardRun; access: WizardRunAccess }>
  list(opts?: { all?: boolean }): Promise<WizardRun[]>
  discard(runId: string): Promise<void>
  /** Activate a `suspended` run (a `waiting` one comes back unchanged). */
  resume(runId: string): Promise<WizardRun | null>
  /** An assignee hands the child back with its outcome and outputs. */
  handback(req: WizardRunHandbackRequest): Promise<void>
}

/** The server run store for one wizard, over the execute funnel. */
export function serverRunStore(rt: Pick<TxnScreenRuntime, 'execute'>, wizard: string): ServerRunStore {
  return {
    async save(run) {
      const res = await rt.execute(wizard, 'wizard_run_save', { run }) as { ok?: boolean; run_id?: string }
      return String(res?.run_id || run.run_id)
    },
    async load(runId) {
      const res = await rt.execute(wizard, 'wizard_run_load', { run_id: runId }) as WizardRunLoadResponse
      if (!res?.run) throw new Error('That wizard run is no longer available.')
      return { run: res.run, access: res.access || 'owner' }
    },
    async list(opts = {}) {
      const res = await rt.execute(wizard, 'wizard_run_list', opts.all ? { all: true } : {}) as WizardRunListResponse
      return res?.runs || []
    },
    async discard(runId) {
      await rt.execute(wizard, 'wizard_run_discard', { run_id: runId })
    },
    async resume(runId) {
      const res = await rt.execute(wizard, 'wizard_run_resume', { run_id: runId }) as { ok?: boolean; run?: WizardRun }
      return res?.run || null
    },
    async handback(req) {
      await rt.execute(wizard, 'wizard_run_handback', { ...req })
    },
  }
}

/** Whether a run must be written to the server now (§9): the wizard keeps
 *  server runs and either something durable happened (a call, a suspend) or
 *  the run is already on the server and its step changed. An assignee's run
 *  always lives on the server (that is how it reached them). */
export function shouldPersist(
  suspend: 'memory' | 'server' | undefined,
  reason: 'call' | 'suspend' | 'step',
  persisted: boolean,
  assignee = false,
): boolean {
  if (assignee) return true
  if (suspend !== 'server') return false
  if (reason === 'call' || reason === 'suspend') return true
  return persisted
}
